import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
    FolderTree,
    HardDrive,
    Mail,
    Radio,
    Settings as SettingsIcon,
    SlidersHorizontal,
    UserRound,
    UsersRound,
    X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { SettingsTab } from '../rail/AvatarMenu';
import { AccountTab } from './tabs/AccountTab';
import { PlaybackTab } from './tabs/PlaybackTab';
import { PodcastsTab } from './tabs/PodcastsTab';
import { InterfaceTab } from './tabs/InterfaceTab';
import { LibraryTab } from './tabs/LibraryTab';
import { UsersTab } from './tabs/UsersTab';
import { EmailTab } from './tabs/EmailTab';
import { NetworkTab } from './tabs/NetworkTab';
import './SettingsModal.css';

interface Props {
    activeTab: SettingsTab;
    onChangeTab: (tab: SettingsTab) => void;
    onClose: () => void;
}

interface NavItem {
    key: SettingsTab;
    label: string;
    icon: LucideIcon;
}

const NAV_GROUPS: { label: string; items: NavItem[] }[] = [
    {
        label: 'You',
        items: [
            { key: 'profile', label: 'Account', icon: UserRound },
            { key: 'playback', label: 'Playback', icon: SlidersHorizontal },
            { key: 'podcasts', label: 'Podcasts', icon: Radio },
        ],
    },
    {
        label: 'Server',
        items: [
            { key: 'library', label: 'Library', icon: FolderTree },
            { key: 'users', label: 'Users', icon: UsersRound },
            { key: 'email', label: 'Email (SMTP)', icon: Mail },
            { key: 'network', label: 'Storage & network', icon: HardDrive },
        ],
    },
];

const TAB_META: Record<SettingsTab, { title: string; desc: string }> = {
    profile: { title: 'Your account', desc: 'How you appear on this server.' },
    playback: { title: 'Playback', desc: 'How Musiceer sounds on this device.' },
    podcasts: { title: 'Podcasts', desc: 'Feeds, downloads and episode playback.' },
    interface: { title: 'Interface', desc: 'Browsing defaults like items per page.' },
    library: { title: 'Library', desc: 'Where your music lives and how often it is indexed.' },
    users: { title: 'Users', desc: 'Who can reach this server.' },
    email: { title: 'Email (SMTP)', desc: 'Used for invites, password resets and server alerts.' },
    network: { title: 'Storage & network', desc: 'Disk, cache and remote access.' },
};

const TAB_COMPONENTS: Record<SettingsTab, React.ComponentType> = {
    profile: AccountTab,
    playback: PlaybackTab,
    podcasts: PodcastsTab,
    interface: InterfaceTab,
    library: LibraryTab,
    users: UsersTab,
    email: EmailTab,
    network: NetworkTab,
};

export function SettingsModal({ activeTab, onChangeTab, onClose }: Props): React.JSX.Element {
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const { title, desc } = TAB_META[activeTab];
    const TabComponent = TAB_COMPONENTS[activeTab];

    return createPortal(
        <div
            className="settingsOverlay"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="settingsCard">
                <nav className="settingsNav">
                    <div className="settingsNavHeader">
                        <div className="settingsNavIcon">
                            <SettingsIcon size={18} strokeWidth={2.75} />
                        </div>
                        <div className="settingsNavTitle">Settings</div>
                    </div>
                    {NAV_GROUPS.map((group) => (
                        <div className="settingsNavGroup" key={group.label}>
                            <div className="settingsNavGroupLabel">{group.label}</div>
                            {group.items.map(({ key, label, icon: Icon }) => (
                                <div
                                    key={key}
                                    className={`settingsNavItem ${activeTab === key ? 'settingsNavItemActive' : ''}`}
                                    onClick={() => onChangeTab(key)}
                                >
                                    <Icon size={17} strokeWidth={2.75} />
                                    {label}
                                </div>
                            ))}
                        </div>
                    ))}
                </nav>

                <div className="settingsContent">
                    <header className="settingsContentHeader">
                        <div className="settingsContentHeaderText">
                            <h2 className="settingsContentTitle">{title}</h2>
                            <div className="settingsContentDesc">{desc}</div>
                        </div>
                        <button className="settingsCloseButton" onClick={onClose} aria-label="Close settings">
                            <X size={19} strokeWidth={2.75} />
                        </button>
                    </header>
                    <div className="settingsContentBody">
                        <TabComponent />
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
